import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Paper,
  Stack,
  Divider,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Edit,
  Upload,
  Download,
  Refresh,
  AttachMoney,
  CheckCircle,
  Cancel,
  Warning,
  Store,
  Gavel,
  Email,
  Schedule,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { VendorPricingSourceSummary } from '../components/vendorPricing/VendorPricingSourceSummary';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { format, subDays, subHours } from 'date-fns';
import { useVendorStatus } from '../hooks/useVendorStatus';
import { DateField, InlineMessage, NumberField, PageContainer, PageHeader } from '../components/ui';
import { getVendorMapAuthority, VENDOR_MAP_AUTHORITY_QUERY_KEY } from '../services/carryRequestsApi';
import { useUrlTab } from '../hooks/useUrlTab';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { VendorSaleWindowsTab } from '../components/saleWindows/VendorSaleWindowsTab';
import { useToast } from '../contexts/ToastContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { tokens } from '../theme/tokens';

interface MapPricingRecord {
  id: string;
  brand: string;
  model: string;
  sku?: string;
  mapPrice: number;
  effectiveDate: Date | null;
  endDate: Date | null;
  description?: string;
  isActive: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
}

interface MapViolation {
  id: string;
  retailerName: string;
  retailerLogo?: string;
  productModel: string;
  productSku: string;
  mapPrice: number;
  advertisedPrice: number;
  violationAmount: number;
  violationPercent: number;
  detectedAt: Date;
  status: 'new' | 'notified' | 'resolved' | 'escalated';
  sourceUrl?: string;
}

// Demo MAP pricing records
const DEMO_MAP_RECORDS: MapPricingRecord[] = [
  { id: '1', brand: 'Demo Brand', model: 'Pro Camera X1', sku: 'PCX1-BLK', mapPrice: 2499.99, effectiveDate: new Date('2026-01-01'), endDate: null, description: 'Flagship mirrorless camera - 45MP full frame', isActive: true, createdAt: new Date('2025-12-15'), updatedAt: new Date('2026-01-01') },
  { id: '2', brand: 'Demo Brand', model: 'Pro Camera X1 Kit', sku: 'PCX1-KIT', mapPrice: 3299.99, effectiveDate: new Date('2026-01-01'), endDate: null, description: 'With 24-70mm f/2.8 lens kit', isActive: true, createdAt: new Date('2025-12-15'), updatedAt: new Date('2026-01-01') },
  { id: '3', brand: 'Demo Brand', model: 'Pro Camera X1 Body Only', sku: 'PCX1-BODY', mapPrice: 2299.99, effectiveDate: new Date('2026-01-01'), endDate: null, description: 'Body only - no lens included', isActive: true, createdAt: new Date('2025-12-15'), updatedAt: new Date('2026-01-01') },
  { id: '4', brand: 'Demo Brand', model: 'Ultra Lens 50mm f/1.4', sku: 'UL50-14', mapPrice: 899.99, effectiveDate: new Date('2026-01-15'), endDate: null, description: 'Premium prime lens - portrait', isActive: true, createdAt: new Date('2026-01-10'), updatedAt: new Date('2026-01-15') },
  { id: '5', brand: 'Demo Brand', model: 'Ultra Lens 24-70mm f/2.8', sku: 'UL2470-28', mapPrice: 1299.99, effectiveDate: new Date('2026-01-15'), endDate: null, description: 'Professional zoom lens', isActive: true, createdAt: new Date('2026-01-10'), updatedAt: new Date('2026-01-15') },
  { id: '6', brand: 'Demo Brand', model: 'Ultra Lens 70-200mm f/2.8', sku: 'UL70200-28', mapPrice: 1799.99, effectiveDate: new Date('2026-01-15'), endDate: null, description: 'Telephoto zoom - sports/wildlife', isActive: true, createdAt: new Date('2026-01-10'), updatedAt: new Date('2026-01-15') },
  { id: '7', brand: 'Demo Brand', model: 'Ultra Lens 14-24mm f/2.8', sku: 'UL1424-28', mapPrice: 1499.99, effectiveDate: new Date('2026-01-15'), endDate: null, description: 'Ultra-wide zoom - landscape', isActive: true, createdAt: new Date('2026-01-10'), updatedAt: new Date('2026-01-15') },
  { id: '8', brand: 'Demo Brand', model: 'Speedlight Pro 600', sku: 'SP600', mapPrice: 549.99, effectiveDate: new Date('2026-02-01'), endDate: null, description: 'Professional TTL flash unit', isActive: true, createdAt: new Date('2026-01-25'), updatedAt: new Date('2026-02-01') },
  { id: '9', brand: 'Demo Brand', model: 'Speedlight Pro 400', sku: 'SP400', mapPrice: 349.99, effectiveDate: new Date('2026-02-01'), endDate: null, description: 'Compact TTL flash unit', isActive: true, createdAt: new Date('2026-01-25'), updatedAt: new Date('2026-02-01') },
  { id: '10', brand: 'Demo Brand', model: 'Battery Grip BG-X1', sku: 'BGX1', mapPrice: 299.99, effectiveDate: new Date('2026-01-01'), endDate: null, description: 'Battery grip for Pro Camera X1', isActive: true, createdAt: new Date('2025-12-15'), updatedAt: new Date('2026-01-01') },
  { id: '11', brand: 'Demo Brand', model: 'Entry Camera E100', sku: 'ECE100', mapPrice: 799.99, effectiveDate: new Date('2025-11-01'), endDate: new Date('2026-03-31'), description: 'Spring promotion pricing', isActive: true, createdAt: new Date('2025-10-15'), updatedAt: new Date('2025-11-01') },
  { id: '12', brand: 'Demo Brand', model: 'Entry Camera E100 Kit', sku: 'ECE100-KIT', mapPrice: 999.99, effectiveDate: new Date('2025-11-01'), endDate: new Date('2026-03-31'), description: 'With 18-55mm kit lens - Spring promo', isActive: true, createdAt: new Date('2025-10-15'), updatedAt: new Date('2025-11-01') },
];

// Demo MAP violations
const DEMO_VIOLATIONS: MapViolation[] = [
  { 
    id: 'v1', 
    retailerName: 'Discount Camera Outlet', 
    productModel: 'Pro Camera X1', 
    productSku: 'PCX1-BLK',
    mapPrice: 2499.99, 
    advertisedPrice: 2299.00, 
    violationAmount: 200.99,
    violationPercent: 8.04,
    detectedAt: subHours(new Date(), 3),
    status: 'new',
    sourceUrl: 'https://example.com/product/pcx1'
  },
  { 
    id: 'v2', 
    retailerName: 'Camera Deals Direct', 
    productModel: 'Pro Camera X1 Kit', 
    productSku: 'PCX1-KIT',
    mapPrice: 3299.99, 
    advertisedPrice: 2999.99, 
    violationAmount: 300.00,
    violationPercent: 9.09,
    detectedAt: subHours(new Date(), 8),
    status: 'notified',
    sourceUrl: 'https://example.com/kit-deal'
  },
  { 
    id: 'v3', 
    retailerName: 'BargainPhoto.com', 
    productModel: 'Ultra Lens 50mm f/1.4', 
    productSku: 'UL50-14',
    mapPrice: 899.99, 
    advertisedPrice: 799.00, 
    violationAmount: 100.99,
    violationPercent: 11.22,
    detectedAt: subDays(new Date(), 1),
    status: 'escalated',
    sourceUrl: 'https://example.com/lens-sale'
  },
  { 
    id: 'v4', 
    retailerName: 'PhotoGear Express', 
    productModel: 'Speedlight Pro 600', 
    productSku: 'SP600',
    mapPrice: 549.99, 
    advertisedPrice: 479.99, 
    violationAmount: 70.00,
    violationPercent: 12.73,
    detectedAt: subDays(new Date(), 2),
    status: 'resolved',
    sourceUrl: 'https://example.com/flash'
  },
  { 
    id: 'v5', 
    retailerName: 'Mega Photo Warehouse', 
    productModel: 'Pro Camera X1', 
    productSku: 'PCX1-BLK',
    mapPrice: 2499.99, 
    advertisedPrice: 2399.00, 
    violationAmount: 100.99,
    violationPercent: 4.04,
    detectedAt: subDays(new Date(), 3),
    status: 'notified',
    sourceUrl: 'https://example.com/warehouse'
  },
];

const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
};

export const VendorBrandMapManagementPage: React.FC = () => {
  const { userProfile } = useAuth();
  const {
    getEffectiveUserType,
    isImpersonating,
    impersonatedRole,
    impersonatedBrand,
    impersonatedOrg,
  } = useImpersonation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const dialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MapPricingRecord | null>(null);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    sku: '',
    mapPrice: '',
    effectiveDate: '',
    endDate: '',
    description: '',
  });
  const [error, setError] = useState<string | null>(null);
  const { t } = useAppTranslation();
  const { enabled: saleWindowsEnabled } = useOrgFeatureFlag('vendor_sale_windows');
  // The sale-windows key is accepted from the URL only while the flag is on, so a
  // stale deep link falls back to pricing instead of selecting a hidden tab.
  const mapTabs: readonly ('pricing' | 'violations' | 'sale-windows')[] = saleWindowsEnabled
    ? ['pricing', 'violations', 'sale-windows']
    : ['pricing', 'violations'];
  const [activeTab, setActiveTab] = useUrlTab(mapTabs, 'pricing');
  const [pricingPage, setPricingPage] = useState(0);
  const [pricingRowsPerPage, setPricingRowsPerPage] = useState(25);
  const [violationsPage, setViolationsPage] = useState(0);
  const [violationsRowsPerPage, setViolationsRowsPerPage] = useState(25);
  const { showToast } = useToast();

  const effectiveUserType = getEffectiveUserType(userProfile);
  const { organization, isPendingApproval: rawIsPendingApproval, isRejected: rawIsRejected, isLoading: statusLoading } = useVendorStatus();
  
  // Demo mode: admin impersonating vendor without a specific brand/org
  const isDemoMode =
    isImpersonating &&
    impersonatedRole === 'vendor' &&
    !impersonatedBrand?.id &&
    !impersonatedOrg?.id &&
    !organization?.id;
  
  // In demo mode, override approval/status flags
  const isPendingApproval = isDemoMode ? false : rawIsPendingApproval;
  const isRejected = isDemoMode ? false : rawIsRejected;

  // Fetch MAP pricing records (only if approved and not in demo mode)
  const { data: mapPricingData, isLoading } = useQuery({
    queryKey: ['vendor-map-pricing'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor/map-pricing', { headers });
      if (!response.ok) {
        throw new Error('Failed to fetch MAP pricing records');
      }
      return response.json();
    },
    enabled: !isDemoMode && !isPendingApproval && !isRejected && effectiveUserType === 'vendor_supplier',
  });

  // Create/Update mutation (must be before any early returns)
  const createUpdateMutation = useMutation({
    mutationFn: async (data: any) => {
      const headers = await getAuthHeaders();
      const url = editingRecord
        ? `/vendor/map-pricing/${editingRecord.id}`
        : '/vendor/map-pricing';
      const method = editingRecord ? 'PUT' : 'POST';
      
      const response = await makeApiCall(url, {
        method,
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save MAP pricing');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-map-pricing'] });
      setOpenDialog(false);
      setEditingRecord(null);
      setFormData({
        brand: '',
        model: '',
        sku: '',
        mapPrice: '',
        effectiveDate: '',
        endDate: '',
        description: '',
      });
      setError(null);
    },
    onError: (error: any) => {
      setError(error.message || 'Failed to save MAP pricing');
    },
  });

  // MOT-406: carried brands Motzu granted this distributor MAP authority for.
  const { enabled: mapAuthorityFlag } = useOrgFeatureFlag('distributor_map_authority');
  const { data: mapAuthority } = useQuery({
    queryKey: VENDOR_MAP_AUTHORITY_QUERY_KEY,
    queryFn: getVendorMapAuthority,
    enabled: mapAuthorityFlag && !isDemoMode && !isPendingApproval && !isRejected && effectiveUserType === 'vendor_supplier',
  });
  const authorityBrands = mapAuthority?.enabled ? mapAuthority.brands : [];
  const brandOptions = authorityBrands.length
    ? [...(mapAuthority?.ownBrandName ? [mapAuthority.ownBrandName] : []), ...authorityBrands.map((b) => b.brandName)]
    : [];

  // Use demo data when in demo mode
  const mapPricingRecords: MapPricingRecord[] = isDemoMode ? DEMO_MAP_RECORDS : (mapPricingData?.data || []);
  const mapViolations: MapViolation[] = isDemoMode ? DEMO_VIOLATIONS : [];

  // Count violations by status
  const newViolations = mapViolations.filter(v => v.status === 'new').length;

  // The backend has no limit/offset support for these lists, so we paginate client-side.
  const paginatedMapPricingRecords = mapPricingRecords.slice(
    pricingPage * pricingRowsPerPage,
    pricingPage * pricingRowsPerPage + pricingRowsPerPage
  );
  const handleChangePricingPage = (_event: unknown, newPage: number) => {
    setPricingPage(newPage);
  };
  const handleChangePricingRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPricingRowsPerPage(parseInt(event.target.value, 10));
    setPricingPage(0);
  };

  const paginatedViolations = mapViolations.slice(
    violationsPage * violationsRowsPerPage,
    violationsPage * violationsRowsPerPage + violationsRowsPerPage
  );
  const handleChangeViolationsPage = (_event: unknown, newPage: number) => {
    setViolationsPage(newPage);
  };
  const handleChangeViolationsRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setViolationsRowsPerPage(parseInt(event.target.value, 10));
    setViolationsPage(0);
  };

  // Check access permissions
  if (effectiveUserType !== 'vendor_supplier') {
    return (
      <PageContainer>
        <Typography variant="h4" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1">
          This page is only accessible to vendor and supplier accounts.
        </Typography>
      </PageContainer>
    );
  }

  // Account lockout for rejected vendors
  if (isRejected) {
    return (
      <PageContainer>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Account Locked
          </Typography>
          <Typography variant="body1">
            Your vendor application has been rejected. Your account access has been suspended.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            If you believe this is an error, please contact support.
          </Typography>
        </Alert>
      </PageContainer>
    );
  }

  const handleOpenDialog = (record?: MapPricingRecord) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        brand: record.brand || '',
        model: record.model,
        sku: record.sku || '',
        mapPrice: record.mapPrice.toString(),
        effectiveDate: record.effectiveDate
          ? format(new Date(record.effectiveDate), 'yyyy-MM-dd')
          : '',
        endDate: record.endDate
          ? format(new Date(record.endDate), 'yyyy-MM-dd')
          : '',
        description: record.description || '',
      });
    } else {
      setEditingRecord(null);
      setFormData({
        brand: brandOptions[0] || '',
        model: '',
        sku: '',
        mapPrice: '',
        effectiveDate: '',
        endDate: '',
        description: '',
      });
    }
    setError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecord(null);
    setError(null);
  };

  const handleSubmit = () => {
    if (!formData.model || !formData.mapPrice || !formData.effectiveDate) {
      setError('Model, MAP price, and effective date are required');
      return;
    }

    const submitData: any = {
      model: formData.model,
      mapPrice: formData.mapPrice,
      effectiveDate: formData.effectiveDate,
    };

    if (!editingRecord && brandOptions.length && formData.brand) submitData.brand = formData.brand;
    if (formData.sku) submitData.sku = formData.sku;
    if (formData.endDate) submitData.endDate = formData.endDate;
    if (formData.description) submitData.description = formData.description;

    if (editingRecord) {
      submitData.isActive = editingRecord.isActive;
    }

    createUpdateMutation.mutate(submitData);
  };

  const handleExport = () => {
    // TODO: Implement CSV export
    showToast('Export functionality coming soon', 'info');
  };

  const getViolationStatusColor = (status: MapViolation['status']) => {
    switch (status) {
      case 'new': return 'error';
      case 'notified': return 'warning';
      case 'escalated': return 'error';
      case 'resolved': return 'success';
      default: return 'default';
    }
  };

  const getViolationStatusIcon = (status: MapViolation['status']) => {
    switch (status) {
      case 'new': return <Warning />;
      case 'notified': return <Email />;
      case 'escalated': return <Gavel />;
      case 'resolved': return <CheckCircle />;
      default: return <Warning />;
    }
  };

  return (
    <PageContainer>
      <PageHeader title="MAP Management" />

      {/* Demo Mode Banner */}
      {isDemoMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Demo Mode
          </Typography>
          <Typography variant="body2">
            This is a preview of the MAP Management features with sample data. In production, vendors can manage their MAP pricing and monitor violations across retail partners.
          </Typography>
        </Alert>
      )}

      {/* Application Under Review Banner */}
      {isPendingApproval && !isDemoMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            Application Under Review
          </Typography>
          <Typography variant="body2">
            Your vendor application is being reviewed. You can preview this page, but actions are disabled until your application is approved.
          </Typography>
        </Alert>
      )}

      {/* Tabs for MAP Pricing vs Violations */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="MAP Pricing" icon={<AttachMoney />} iconPosition="start" value="pricing" />
          <Tab
            label={
              <Badge badgeContent={newViolations} color="error" max={99}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning />
                  MAP Violations
                </Box>
              </Badge>
            }
            value="violations"
          />
          {saleWindowsEnabled && <Tab label={t('vendor.saleWindows.tab')} value="sale-windows" />}
        </Tabs>
      </Box>

      {activeTab === 'pricing' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Manage MAP (Minimum Advertised Price) for your products. MAP pricing will be automatically propagated to all matching products.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={handleExport}
                disabled={isPendingApproval && !isDemoMode}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Import
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExport}
                disabled={isPendingApproval && !isDemoMode}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Export
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                disabled={isPendingApproval && !isDemoMode}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Add MAP Pricing
              </Button>
            </Box>
          </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {authorityBrands.length > 0 && (
        <InlineMessage severity="info">
          <strong>{t('vendor.mapAuthority.title')}</strong>
          {' · '}
          {t('vendor.mapAuthority.intro', { brands: authorityBrands.map((b) => b.brandName).join(', ') })}
        </InlineMessage>
      )}

      {!isDemoMode && !isPendingApproval && !isRejected && effectiveUserType === 'vendor_supplier' && (
        <VendorPricingSourceSummary kind="map" enabled />
      )}

      <Card>
        <CardContent>
          {!isDemoMode && statusLoading ? (
            <Typography>Loading...</Typography>
          ) : !isDemoMode && isLoading ? (
            <Typography>Loading MAP pricing records...</Typography>
          ) : isPendingApproval ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AttachMoney sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                MAP Management Preview
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Once your vendor application is approved, you'll be able to manage MAP pricing for your products here. 
                MAP pricing will automatically propagate to all matching products across the platform.
              </Typography>
            </Box>
          ) : mapPricingRecords.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AttachMoney sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No MAP Pricing Records
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add your first MAP pricing record to get started.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                disabled={isPendingApproval}
              >
                Add MAP Pricing
              </Button>
            </Box>
          ) : isMobile ? (
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              <Stack divider={<Divider />}>
                {paginatedMapPricingRecords.map((record) => (
                  <Box key={record.id} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2">
                          {brandOptions.length > 0 ? `${record.brand} · ${record.model}` : record.model}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{record.sku || '—'}</Typography>
                      </Box>
                      <Chip
                        label={record.isActive ? 'Active' : 'Inactive'}
                        color={record.isActive ? 'success' : 'default'}
                        size="small"
                        icon={record.isActive ? <CheckCircle /> : <Cancel />}
                        sx={{ flexShrink: 0 }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>MAP Price</Typography>
                        <Typography variant="body2">${record.mapPrice.toFixed(2)}</Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Effective</Typography>
                        <Typography variant="body2">
                          {record.effectiveDate ? format(new Date(record.effectiveDate), 'MMM dd, yyyy') : '—'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>End Date</Typography>
                        <Typography variant="body2">
                          {record.endDate ? format(new Date(record.endDate), 'MMM dd, yyyy') : 'None'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleOpenDialog(record)}
                        disabled={isPendingApproval}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {brandOptions.length > 0 && <TableCell>{t('vendor.mapAuthority.brandColumn')}</TableCell>}
                    <TableCell>Model</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>MAP Price</TableCell>
                    <TableCell>Effective Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedMapPricingRecords.map((record) => (
                    <TableRow key={record.id}>
                      {brandOptions.length > 0 && <TableCell>{record.brand}</TableCell>}
                      <TableCell>{record.model}</TableCell>
                      <TableCell>{record.sku || '—'}</TableCell>
                      <TableCell>${record.mapPrice.toFixed(2)}</TableCell>
                      <TableCell>
                        {record.effectiveDate
                          ? format(new Date(record.effectiveDate), 'MMM dd, yyyy')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {record.endDate
                          ? format(new Date(record.endDate), 'MMM dd, yyyy')
                          : 'No end date'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={record.isActive ? 'Active' : 'Inactive'}
                          color={record.isActive ? 'success' : 'default'}
                          size="small"
                          icon={record.isActive ? <CheckCircle /> : <Cancel />}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(record)}
                            disabled={isPendingApproval}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!isPendingApproval && mapPricingRecords.length > 0 && (
            <TablePagination
              component="div"
              count={mapPricingRecords.length}
              page={pricingPage}
              onPageChange={handleChangePricingPage}
              rowsPerPage={pricingRowsPerPage}
              onRowsPerPageChange={handleChangePricingRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
            />
          )}
        </CardContent>
      </Card>
        </>
      )}

      {/* MAP Violations Tab */}
      {activeTab === 'sale-windows' && saleWindowsEnabled && (
        <VendorSaleWindowsTab readOnly={isDemoMode || isPendingApproval || isRejected} />
      )}

      {activeTab === 'violations' && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Monitor retailers advertising your products below MAP. Our automated system scans retail websites and marketplaces to detect violations.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                disabled={!isDemoMode}
              >
                Refresh Scan
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                disabled={!isDemoMode}
              >
                Export Report
              </Button>
            </Box>
          </Box>

          {/* Violation Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(200px, 1fr))' }, gap: 2, mb: 3 }}>
            <Card sx={{ bgcolor: 'error.50', borderLeft: 4, borderColor: 'error.main' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning color="error" />
                  <Typography variant="h4" color="error.main" fontWeight="bold">
                    {mapViolations.filter(v => v.status === 'new').length}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">New Violations</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'warning.50', borderLeft: 4, borderColor: 'warning.main' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email color="warning" />
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {mapViolations.filter(v => v.status === 'notified').length}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Notified</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'error.50', borderLeft: 4, borderColor: 'error.dark' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Gavel color="error" />
                  <Typography variant="h4" color="error.dark" fontWeight="bold">
                    {mapViolations.filter(v => v.status === 'escalated').length}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Escalated</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: 'success.50', borderLeft: 4, borderColor: 'success.main' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="success" />
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {mapViolations.filter(v => v.status === 'resolved').length}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Resolved</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Violations Table */}
          <Card>
            <CardContent>
              {mapViolations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No MAP Violations Detected
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    All monitored retailers are currently advertising your products at or above MAP.
                  </Typography>
                </Box>
              ) : isMobile ? (
                <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                  <Stack divider={<Divider />}>
                    {paginatedViolations.map((violation) => (
                      <Box
                        key={violation.id}
                        sx={{ p: 2, bgcolor: violation.status === 'new' ? 'error.50' : 'transparent' }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2">{violation.retailerName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {violation.productModel} · {violation.productSku}
                            </Typography>
                          </Box>
                          <Chip
                            icon={getViolationStatusIcon(violation.status)}
                            label={violation.status.charAt(0).toUpperCase() + violation.status.slice(1)}
                            size="small"
                            color={getViolationStatusColor(violation.status) as any}
                            variant={violation.status === 'new' ? 'filled' : 'outlined'}
                            sx={{ flexShrink: 0 }}
                          />
                        </Stack>
                        <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>MAP Price</Typography>
                            <Typography variant="body2">${violation.mapPrice.toFixed(2)}</Typography>
                          </Box>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>Advertised</Typography>
                            <Typography variant="body2" color="error.main">${violation.advertisedPrice.toFixed(2)}</Typography>
                          </Box>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>Violation</Typography>
                            <Typography variant="body2" color="error.main" fontWeight="bold">
                              -${violation.violationAmount.toFixed(2)} ({violation.violationPercent.toFixed(1)}%)
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Detected {format(new Date(violation.detectedAt), 'MMM dd, h:mm a')}
                        </Typography>
                        <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mt: 1 }}>
                          <Tooltip title="Send Warning">
                            <IconButton size="small" disabled={!isDemoMode}>
                              <Email fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Escalate">
                            <IconButton size="small" disabled={!isDemoMode}>
                              <Gavel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark Resolved">
                            <IconButton size="small" disabled={!isDemoMode}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Retailer</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">MAP Price</TableCell>
                        <TableCell align="right">Advertised</TableCell>
                        <TableCell align="right">Violation</TableCell>
                        <TableCell>Detected</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedViolations.map((violation) => (
                        <TableRow 
                          key={violation.id}
                          sx={{ 
                            bgcolor: violation.status === 'new' ? 'error.50' : 'inherit',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200' }}>
                                <Store fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" fontWeight="medium">
                                {violation.retailerName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{violation.productModel}</Typography>
                            <Typography variant="caption" color="text.secondary">{violation.productSku}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              ${violation.mapPrice.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error.main" fontWeight="medium">
                              ${violation.advertisedPrice.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box>
                              <Typography variant="body2" color="error.main" fontWeight="bold">
                                -${violation.violationAmount.toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="error.main">
                                ({violation.violationPercent.toFixed(1)}% below)
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Schedule fontSize="small" color="action" />
                              <Typography variant="body2">
                                {format(new Date(violation.detectedAt), 'MMM dd, h:mm a')}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getViolationStatusIcon(violation.status)}
                              label={violation.status.charAt(0).toUpperCase() + violation.status.slice(1)}
                              size="small"
                              color={getViolationStatusColor(violation.status) as any}
                              variant={violation.status === 'new' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Send Warning">
                                <IconButton size="small" disabled={!isDemoMode}>
                                  <Email fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Escalate">
                                <IconButton size="small" disabled={!isDemoMode}>
                                  <Gavel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Mark Resolved">
                                <IconButton size="small" disabled={!isDemoMode}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {mapViolations.length > 0 && (
                <TablePagination
                  component="div"
                  count={mapViolations.length}
                  page={violationsPage}
                  onPageChange={handleChangeViolationsPage}
                  rowsPerPage={violationsRowsPerPage}
                  onRowsPerPageChange={handleChangeViolationsRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              )}
            </CardContent>
          </Card>

          {/* Future Features Notice */}
          {isDemoMode && (
            <Alert severity="info" sx={{ mt: 3 }} icon={<Schedule />}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Coming Soon: Automated MAP Enforcement
              </Typography>
              <Typography variant="body2">
                Future features will include: automated retailer notifications, scheduled compliance reports, 
                integration with legal/compliance workflows, historical violation tracking, and retailer compliance scores.
              </Typography>
            </Alert>
          )}
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={dialogFullScreen}>
        <DialogTitle>
          {editingRecord ? 'Edit MAP Pricing' : 'Add MAP Pricing'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {brandOptions.length > 0 && (
              <TextField
                select
                label={t('vendor.mapAuthority.brandLabel')}
                fullWidth
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                disabled={!!editingRecord}
                helperText={t('vendor.mapAuthority.brandHelper')}
              >
                {(editingRecord && formData.brand && !brandOptions.includes(formData.brand)
                  ? [formData.brand, ...brandOptions]
                  : brandOptions
                ).map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Product Model"
              required
              fullWidth
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              disabled={!!editingRecord}
              helperText={editingRecord ? 'Model cannot be changed' : 'Enter the product model name'}
            />
            <TextField
              label="SKU (Optional)"
              fullWidth
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            />
            <NumberField
              label="MAP Price"
              adornment="$"
              min={0}
              step="0.01"
              value={formData.mapPrice}
              onChange={(v) => setFormData({ ...formData, mapPrice: v })}
              sx={{ width: '100%' }}
            />
            <DateField
              label="Effective Date"
              value={formData.effectiveDate}
              onChange={(v) => setFormData({ ...formData, effectiveDate: v })}
              sx={{ width: '100%' }}
            />
            <DateField
              label="End Date (Optional)"
              value={formData.endDate}
              min={formData.effectiveDate || undefined}
              onChange={(v) => setFormData({ ...formData, endDate: v })}
              sx={{ width: '100%' }}
            />
            <TextField
              label="Description (Optional)"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            {editingRecord && (
              <Alert severity="info">
                Changes will automatically propagate to all products matching this model.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createUpdateMutation.isPending}
          >
            {createUpdateMutation.isPending ? 'Saving...' : editingRecord ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

